import { Card, CardStatus, CardType } from "@wingspanhq/wingspan-api-sdk";
import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import React from "react";
import { useHistory } from "react-router-dom";
import {
  WALLET_ACTIVATE_VIRTUAL_CARD_PATH,
  WALLET_ORDER_PHYSICAL_CARD_PATH
} from "../../index";
import { useActivateCardModal } from "../../modals/ActivateCard/useActivateCardModal";
import { useCardActivatedModal } from "../../modals/ActivateCard/useCardActivatedModal";
import { useConfirmReceivingCardModal } from "../../modals/ActivateCard/useConfirmRecivingCardModal";
import { useModalAddCardToDigitalWallet } from "../../modals/ModalAddCardToDigitalWallet";
import { useCardReactivatedModal } from "../../modals/ReactivateCard/useCardReactivatedModal";
import { useReactivateCardModal } from "../../modals/ReactivateCard/useReactivateCardModal";
import { selectorCardName } from "../../selectors/selectorCardName";
import { ActiveCard } from "./ActiveCard";
import { EmptyCardPlaceholder } from "./EmptyCardPlaceholder";
import { ProcessCardPlaceholder } from "./ProcessCardPlaceholder";

type Props = {
  card: Card;
};

export const CardView: React.FC<Props> = ({ card }) => {
  const history = useHistory();
  const { openSnackbar } = useWSSnackbar();

  const cardType = card.cardType === CardType.VIRTUAL ? "virtual" : "physical";
  const cardName = selectorCardName(card);
  const cardId = card.id;

  const reactivateCardModal = useReactivateCardModal();
  const cardReactivatedModal = useCardReactivatedModal();

  const cardActivatedModal = useCardActivatedModal();
  const activateCardModal = useActivateCardModal();
  const confirmReceivingCardModal = useConfirmReceivingCardModal();
  const modalAddCardToDigitalWallet = useModalAddCardToDigitalWallet();

  const cardComponent: Record<CardStatus, React.ReactNode> = {
    [CardStatus.ACTIVE]: <ActiveCard card={card} />,
    [CardStatus.SUSPENDED]: (
      <ProcessCardPlaceholder
        title="Card suspended"
        cardType={cardType}
        label="Reactivate card"
        onAction={() => {
          reactivateCardModal.open({
            cardId,
            cardName,
            onSuccess() {
              openSnackbar({
                message: "Card unsuspended",
                type: "success"
              });
              cardReactivatedModal.open({ cardId, cardName });
            }
          });
        }}
      />
    ),
    [CardStatus.CANCELLED]: (
      <ProcessCardPlaceholder cardType={cardType} title="Card is closed" />
    ),
    [CardStatus.ONE_TIME]: (
      <ProcessCardPlaceholder cardType={cardType} title="One time card" />
    ),
    [CardStatus.PENDING_ACTIVATION]: (
      <EmptyCardPlaceholder
        actionIcon="card"
        actionLabel="Activate card"
        cardType={cardType}
        onAction={() => {
          if (cardType === "physical") {
            confirmReceivingCardModal.open({
              onSubmit() {
                activateCardModal.open({
                  cardId,
                  onSuccess() {
                    openSnackbar({
                      message: "Card activated",
                      type: "success"
                    });
                    cardActivatedModal.open({
                      onContinue: () => {
                        modalAddCardToDigitalWallet.open();
                      }
                    });
                  }
                });
              }
            });
          } else {
            history.push(WALLET_ACTIVATE_VIRTUAL_CARD_PATH);
          }
        }}
      />
    ),
    [CardStatus.PENDING_FULFILLMENT]: (
      <EmptyCardPlaceholder
        actionIcon="shield-check-fill"
        actionLabel="Activate card"
        cardType={cardType}
        onAction={() => {
          if (cardType === "physical") {
            confirmReceivingCardModal.open({
              onSubmit() {
                activateCardModal.open({
                  cardId,
                  onSuccess() {
                    openSnackbar({
                      message: "Card activated",
                      type: "success"
                    });
                    cardActivatedModal.open({
                      onContinue: () => {
                        modalAddCardToDigitalWallet.open();
                      }
                    });
                  }
                });
              }
            });
          } else {
            history.push(WALLET_ACTIVATE_VIRTUAL_CARD_PATH);
          }
        }}
      />
    )
  };

  const placeholderComponent = (
    <EmptyCardPlaceholder
      actionIcon="card"
      actionLabel="Order card"
      cardType={cardType}
      onAction={() => {
        if (cardType === "virtual") {
          history.push(WALLET_ACTIVATE_VIRTUAL_CARD_PATH);
        } else {
          history.push(WALLET_ORDER_PHYSICAL_CARD_PATH);
        }
      }}
    />
  );

  return <>{cardComponent[card.status] || placeholderComponent}</>;
};
